import { FC, useState } from 'react'
import classNames from 'classnames'
import { useAdPerfBreakdown } from 'src/reactQuery/useAdPerformance'
import { AdPerfBreakdownRow, AdPerfRange } from 'src/services/adPerformanceApi'
import s from './dashboard.module.scss'
import { BulbIcon } from './icons'
import { PillSelect } from './PillSelect'
import { compact, money, placementLabel } from '../format'

type Metric = 'clicks' | 'impressions' | 'leads' | 'spend'
const METRICS: { key: Metric; label: string }[] = [
  { key: 'spend', label: 'Spend' },
  { key: 'impressions', label: 'Impressions' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'leads', label: 'Leads' },
]
// Cost per unit of the chosen metric.
const costOf = (r: AdPerfBreakdownRow, m: Metric) =>
  m === 'clicks'
    ? r.cpc
    : m === 'impressions'
      ? r.cpm
      : m === 'leads'
        ? r.cost_per_lead
        : null
const costLabel: Record<Metric, string> = {
  clicks: 'CPC',
  impressions: 'CPM',
  leads: 'Cost per lead',
  spend: '',
}

interface Props {
  currency: string
  range: AdPerfRange
}

export const Placements: FC<Props> = ({ currency, range }) => {
  const [metric, setMetric] = useState<Metric>('clicks')
  // Meta is the only connected platform, so All and Meta show the same data.
  const [platform, setPlatform] = useState('all')
  const { data } = useAdPerfBreakdown(range, 'placement')
  const rows = [...(data?.rows ?? [])]
    .filter((r) => r[metric] > 0)
    .sort((a, b) => b[metric] - a[metric])
    .slice(0, 5)
  const share = (r: AdPerfBreakdownRow) =>
    (r[`share_${metric}` as keyof AdPerfBreakdownRow] as number | null) ?? 0
  const costs = rows
    .map((r) => costOf(r, metric))
    .filter((v): v is number => v !== null)
  const avgCost = costs.length
    ? costs.reduce((a, b) => a + b, 0) / costs.length
    : null
  const fmt = (v: number) =>
    metric === 'spend' ? money(v, currency) : compact(v)
  const unit = METRICS.find((m) => m.key === metric)!.label.toLowerCase()

  // The lowest-cost placement gets the wide card at the top.
  const best =
    metric === 'spend'
      ? rows[0]
      : rows.reduce<AdPerfBreakdownRow | undefined>(
          (a, r) =>
            !a ||
            (costOf(r, metric) ?? Infinity) < (costOf(a, metric) ?? Infinity)
              ? r
              : a,
          undefined
        )
  const ordered = best ? [best, ...rows.filter((r) => r !== best)] : rows

  return (
    <div className={s.card}>
      <div className={s.cardHeader}>
        <h3 className={s.cardTitle}>Placement Comparison</h3>
        <PillSelect
          label="Platform"
          onChange={setPlatform}
          options={[
            { label: 'All', value: 'all' },
            { label: 'Meta', value: 'meta' },
          ]}
          value={platform}
        />
      </div>
      <div className={s.chips} style={{ marginBottom: 16 }}>
        {METRICS.map((m) => (
          <button
            className={classNames(s.chip, { [s.chipActive]: metric === m.key })}
            key={m.key}
            onClick={() => setMetric(m.key)}
            type="button"
          >
            {m.label}
          </button>
        ))}
      </div>
      {!rows.length && (
        <div className={s.empty}>No placement data in this period.</div>
      )}
      <div className={s.placements}>
        {ordered.map((r) => {
          const cost = costOf(r, metric)
          const isBest = r === best
          const aboveAvg =
            !isBest &&
            cost !== null &&
            avgCost !== null &&
            cost > avgCost * 1.25
          const metricLabel = METRICS.find((m) => m.key === metric)!.label
          if (isBest)
            return (
              <div
                className={classNames(s.placement, s.placementBest)}
                key={r.value}
              >
                <div className={s.placementBestTop}>
                  <div>
                    {metric !== 'spend' && (
                      <div className={s.placementLowest}>
                        Lowest {costLabel[metric]}{' '}
                        <strong>{money(cost, currency, 2)}</strong>
                      </div>
                    )}
                    <div className={s.placementBestName}>
                      {placementLabel(r.value)}
                    </div>
                  </div>
                  <div className={s.placementBestValue}>
                    {fmt(r[metric])} <span>/ {unit}</span>
                  </div>
                </div>
                <div className={s.shareRow}>
                  <span>
                    {metricLabel} {share(r)}%
                  </span>
                  <div className={s.progress}>
                    <span style={{ width: `${share(r)}%` }} />
                  </div>
                </div>
              </div>
            )
          return (
            <div
              className={classNames(s.placement, {
                [s.placementWarn]: aboveAvg,
              })}
              key={r.value}
            >
              <div className={s.placementName}>
                <span>{placementLabel(r.value)}</span>
                {aboveAvg && (
                  <span className={s.flag}>
                    Above average {costLabel[metric]}
                  </span>
                )}
              </div>
              <div className={s.placementValue}>
                {fmt(r[metric])} <span>/ {unit}</span>
              </div>
              {metric !== 'spend' && (
                <div className={s.placementCost}>
                  <small>{costLabel[metric]}</small> {money(cost, currency, 2)}
                </div>
              )}
              <div className={s.shareRow}>
                <span>
                  {metricLabel} {share(r)}%
                </span>
                <div className={s.progress}>
                  <span style={{ width: `${share(r)}%` }} />
                </div>
              </div>
            </div>
          )
        })}
      </div>
      {best && metric !== 'spend' && ordered.length > 1 && (
        <div className={s.tip}>
          <BulbIcon />
          <span>
            <strong>
              {placementLabel(best.value)} has the lowest {costLabel[metric]} (
              {money(costOf(best, metric), currency, 2)}).
            </strong>{' '}
            Average across placements is {money(avgCost, currency, 2)}.
          </span>
        </div>
      )}
    </div>
  )
}
